/**
 * Vanilla JS thumbnail navigation for the product-gallery-sidebar section.
 * Clicking a thumbnail swaps the visible main image and updates the active border.
 */
const SWIPE_THRESHOLD_PX = 40;

class ProductGallerySidebar extends HTMLElement {
  activeIndex = 0;
  #swipeStartX = 0;
  #swipeStartY = 0;
  #swiping = false;

  /** @type {HTMLElement[]} */
  thumbs = [];
  /** @type {HTMLElement[]} */
  mains = [];
  /** @type {HTMLElement | null} */
  mainEl = null;

  connectedCallback() {
    this.thumbs = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pgs-thumb]')));
    this.mains = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-pgs-main]')));
    this.mainEl = this.querySelector('.product-gallery-sidebar__main');

    this.addEventListener('click', this.#handleClick);

    if (this.mainEl && this.mains.length > 1) {
      this.mainEl.addEventListener('pointerdown', this.#handlePointerDown);
      this.mainEl.addEventListener('pointerup', this.#handlePointerUp);
      this.mainEl.addEventListener('pointercancel', this.#handlePointerUp);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);

    if (this.mainEl) {
      this.mainEl.removeEventListener('pointerdown', this.#handlePointerDown);
      this.mainEl.removeEventListener('pointerup', this.#handlePointerUp);
      this.mainEl.removeEventListener('pointercancel', this.#handlePointerUp);
    }
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;

    this.#swipeStartX = event.clientX;
    this.#swipeStartY = event.clientY;
    this.#swiping = true;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!this.#swiping) return;
    this.#swiping = false;

    const deltaX = event.clientX - this.#swipeStartX;
    const deltaY = event.clientY - this.#swipeStartY;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) return;

    if (deltaX < 0) {
      this.#setActiveIndex((this.activeIndex + 1) % this.thumbs.length);
    } else {
      this.#setActiveIndex((this.activeIndex - 1 + this.thumbs.length) % this.thumbs.length);
    }
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const thumb = target.closest('[data-pgs-thumb]');
    if (thumb instanceof HTMLElement) {
      this.#setActiveIndex(Number(thumb.dataset.index));
      return;
    }

    if (target.closest('[data-pgs-next]')) {
      this.#setActiveIndex((this.activeIndex + 1) % this.thumbs.length);
      return;
    }

    if (target.closest('[data-pgs-prev]')) {
      this.#setActiveIndex((this.activeIndex - 1 + this.thumbs.length) % this.thumbs.length);
    }
  };

  /** @param {number} index */
  #setActiveIndex(index) {
    this.activeIndex = index;

    this.thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
    });

    this.mains.forEach((main, i) => {
      const active = i === index;
      main.classList.toggle('is-active', active);
      main.setAttribute('aria-hidden', String(!active));
    });

    const activeRatio = this.mains[index]?.dataset.aspectRatio;
    if (activeRatio) {
      this.style.setProperty('--pgs-active-ratio', activeRatio);
    }

    this.thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

if (!customElements.get('product-gallery-sidebar')) {
  customElements.define('product-gallery-sidebar', ProductGallerySidebar);
}
